/**
 * Suggestion chips for the Work-home focused (linked-context) surface.
 *
 * When a patient or session is linked in the composer, the left half
 * collapses to a placeholder + docked composer. These chips fill that
 * space with concrete, tappable starting points tailored to the linked
 * entity — clicking one seeds the composer (via ``onSelect``) exactly
 * like {@link WorkExampleChips} does on the default home.
 *
 * The two sets mirror what the agent can actually do against each
 * entity (patient context / EHR sync / export / referral for patients;
 * note + document + patient-linking for sessions) without naming the
 * underlying tools. Static + source-curated for now, same rationale as
 * ``WorkExampleChips``.
 */

import { Button } from '@heidi/design-system/button';
import { cn } from '@heidi/utils';
import {
  FileDown,
  FilePen,
  FilePlus2,
  Mail,
  NotebookPen,
  RefreshCw,
  Sparkles,
  Upload,
  UserRoundPlus,
  type LucideIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';

import type { WorkContextKind } from '../../../contracts/projects/work-context';
import {
  workContextFormSuggestionMessages as formMessages,
  workContextPatientSuggestionMessages as patientMessages,
  workContextSessionSuggestionMessages as sessionMessages,
  workContextTemplateSuggestionMessages as templateMessages,
} from './WorkContextSuggestions.messages';

// Re-exported rather than declared: `workHomeFocusContext.ts` reads the same
// union from `/data`, which may not reach this zone, so the declaration sits in
// `/contracts`. This path publishes exactly the names it always published.
export type { WorkContextKind };

export interface WorkContextSuggestionsProps {
  kind: WorkContextKind;
  /** Seeds the composer textarea (forwarded to ``prefilledValue``). */
  onSelect: (prompt: string) => void;
  disabled?: boolean;
  className?: string;
}

interface ContextSuggestion {
  label: MessageDescriptor;
  prompt: MessageDescriptor;
  Icon: LucideIcon;
}

const _PATIENT_SUGGESTIONS: readonly ContextSuggestion[] = [
  {
    label: patientMessages.summariseLabel,
    prompt: patientMessages.summarisePrompt,
    Icon: Sparkles,
  },
  {
    label: patientMessages.updateContextLabel,
    prompt: patientMessages.updateContextPrompt,
    Icon: NotebookPen,
  },
  {
    label: patientMessages.syncFromEhrLabel,
    prompt: patientMessages.syncFromEhrPrompt,
    Icon: RefreshCw,
  },
  {
    label: patientMessages.exportToEhrLabel,
    prompt: patientMessages.exportToEhrPrompt,
    Icon: Upload,
  },
  {
    label: patientMessages.exportPdfLabel,
    prompt: patientMessages.exportPdfPrompt,
    Icon: FileDown,
  },
  {
    label: patientMessages.emailLabel,
    prompt: patientMessages.emailPrompt,
    Icon: Mail,
  },
  {
    label: patientMessages.referralLetterLabel,
    prompt: patientMessages.referralLetterPrompt,
    Icon: FilePen,
  },
];

const _TEMPLATE_SUGGESTIONS: readonly ContextSuggestion[] = [
  {
    label: templateMessages.updateTemplateLabel,
    prompt: templateMessages.updateTemplatePrompt,
    Icon: NotebookPen,
  },
  {
    label: templateMessages.addSectionLabel,
    prompt: templateMessages.addSectionPrompt,
    Icon: FilePlus2,
  },
  {
    label: templateMessages.improveWordingLabel,
    prompt: templateMessages.improveWordingPrompt,
    Icon: Sparkles,
  },
  {
    label: templateMessages.createNoteLabel,
    prompt: templateMessages.createNotePrompt,
    Icon: FilePen,
  },
];

const _FORM_SUGGESTIONS: readonly ContextSuggestion[] = [
  {
    label: formMessages.addFieldLabel,
    prompt: formMessages.addFieldPrompt,
    Icon: FilePlus2,
  },
  {
    label: formMessages.updateFieldLabelsLabel,
    prompt: formMessages.updateFieldLabelsPrompt,
    Icon: NotebookPen,
  },
  {
    label: formMessages.explainFieldsLabel,
    prompt: formMessages.explainFieldsPrompt,
    Icon: Sparkles,
  },
  {
    label: formMessages.exportPdfLabel,
    prompt: formMessages.exportPdfPrompt,
    Icon: FileDown,
  },
];

const _SESSION_SUGGESTIONS: readonly ContextSuggestion[] = [
  {
    label: sessionMessages.updateNoteLabel,
    prompt: sessionMessages.updateNotePrompt,
    Icon: NotebookPen,
  },
  {
    label: sessionMessages.createDocumentLabel,
    prompt: sessionMessages.createDocumentPrompt,
    Icon: FilePlus2,
  },
  {
    label: sessionMessages.summariseSessionLabel,
    prompt: sessionMessages.summariseSessionPrompt,
    Icon: Sparkles,
  },
  {
    label: sessionMessages.attachPatientLabel,
    prompt: sessionMessages.attachPatientPrompt,
    Icon: UserRoundPlus,
  },
  {
    label: sessionMessages.createPatientLabel,
    prompt: sessionMessages.createPatientPrompt,
    Icon: UserRoundPlus,
  },
  {
    label: sessionMessages.exportPdfLabel,
    prompt: sessionMessages.exportPdfPrompt,
    Icon: FileDown,
  },
];

export const WorkContextSuggestions = ({
  kind,
  onSelect,
  disabled = false,
  className,
}: WorkContextSuggestionsProps): JSX.Element => {
  const intl = useIntl();
  const suggestions = useMemo(() => {
    switch (kind) {
      case 'patient':
        return _PATIENT_SUGGESTIONS;
      case 'template':
        return _TEMPLATE_SUGGESTIONS;
      case 'form':
        return _FORM_SUGGESTIONS;
      default:
        return _SESSION_SUGGESTIONS;
    }
  }, [kind]);

  return (
    <div
      className={cn(
        'flex w-full max-w-xl flex-wrap items-center justify-center gap-2',
        className
      )}
      data-testid={`work-context-suggestions-${kind}`}
    >
      {suggestions.map((s) => (
        <Button
          key={s.label.id}
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => onSelect(intl.formatMessage(s.prompt))}
          className="bg-surface"
        >
          <s.Icon aria-hidden="true" className="size-3.5 shrink-0 text-text-tertiary" />
          <span>{intl.formatMessage(s.label)}</span>
        </Button>
      ))}
    </div>
  );
};
